// components/InternshipDialog.tsx
import AsyncMultiSelectField from "@/components/form/components/AsyncMultiSelectField";
import AsyncSingleSelectField from "@/components/form/components/AsyncSingleSelectField";
import InputField from "@/components/form/components/InputField";
import SelectField from "@/components/form/components/SelectField";
import TextareaField from "@/components/form/components/Textarea";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Form } from "@/components/ui/form";
import {
  useCreateInternshipMutation,
  useUpdateInternshipMutation,
} from "@/hooks/api/internship.queries";
import { fetchBatchOptions } from "@/services/batch.service";
import { departmentService } from "@/services/department.service";
import { fetchInternVendorOptions } from "@/services/internVendor.service";
import {
  InternshipCreateType,
  InternshipParamsType,
  InternshipType,
} from "@/services/internship.service";
import { fetchStudentOptions } from "@/services/student.service";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import * as z from "zod";

type InternshipDialogProps = {
  mode: "create" | "edit";
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  filters: InternshipParamsType;
  internshipId?: string;
  internshipData?: InternshipType | null;
  onCancel: () => void;
};

const internshipSchema = z.object({
  position: z.string().min(1, "Position Title is required"),
  company: z.string().min(1, "Company is required"),
  students: z.array(z.string()).min(1, "Select at least one student"),
  departments: z.array(z.string()).optional(),
  batches: z.array(z.string()).optional(),
  duration: z.string().min(1, "Duration is required"),
  stipend: z.number().min(0).optional(),
  mode: z.enum(["On-site", "Remote", "Hybrid"]),
  startDate: z.string().min(1, "Start Date is required"),
  endDate: z.string().optional(),
  status: z.enum(["active", "pending", "completed"]),
  description: z.string().optional(),
  requirements: z.string().optional(),
});

type InternshipFormType = z.infer<typeof internshipSchema>;

const MODE_OPTIONS = [
  { label: "On-site", value: "On-site" },
  { label: "Remote", value: "Remote" },
  { label: "Hybrid", value: "Hybrid" },
];

const STATUS_OPTIONS = [
  { label: "Pending", value: "pending" },
  { label: "Active", value: "active" },
  { label: "Completed", value: "completed" },
];

const InternshipDialog = ({
  mode,
  isOpen,
  onOpenChange,
  filters,
  internshipId,
  internshipData,
  onCancel,
}: InternshipDialogProps) => {
  const createMutation = useCreateInternshipMutation(filters);
  const updateMutation = useUpdateInternshipMutation(filters);
  const isPending = createMutation.isPending || updateMutation.isPending;

  const form = useForm<InternshipFormType>({
    resolver: zodResolver(internshipSchema),
    defaultValues: {
      position: "",
      company: "",
      students: [],
      departments: [],
      batches: [],
      duration: "",
      stipend: 0,
      mode: "On-site",
      startDate: "",
      endDate: "",
      status: "pending",
      description: "",
      requirements: "",
    },
  });

  useEffect(() => {
    if (isOpen) {
      if (mode === "edit" && internshipData) {
        setTimeout(() => {
          form.reset({
            position: internshipData.position,
            company: internshipData.company,
            students: internshipData.students || [],
            departments: internshipData.departments || [],
            batches: internshipData.batches || [],
            duration: internshipData.duration,
            stipend: internshipData.stipend || 0,
            mode: internshipData.mode,
            startDate: internshipData.startDate,
            endDate: internshipData.endDate || "",
            status: internshipData.status,
            description: internshipData.description || "",
            requirements: internshipData.requirements || "",
          });
        }, 0);
      } else {
        form.reset({
          position: "",
          company: "",
          students: [],
          departments: [],
          batches: [],
          duration: "",
          stipend: 0,
          mode: "On-site",
          startDate: "",
          endDate: "",
          status: "pending",
          description: "",
          requirements: "",
        });
      }
    }
  }, [mode, internshipData, isOpen, form]);

  const handleSubmit = (values: InternshipFormType) => {
    const submissionData: InternshipCreateType = {
      ...values,
      departments: values.departments || [],
      batches: values.batches || [],
      stipend: values.stipend || 0,
      endDate: values.endDate || "",
      description: values.description || "",
      requirements: values.requirements || "",
    };

    if (mode === "create") {
      createMutation.mutate(submissionData, { onSuccess: onCancel });
    } else if (mode === "edit" && internshipId) {
      updateMutation.mutate(
        { _id: internshipId, data: submissionData },
        { onSuccess: onCancel }
      );
    }
  };

  const defaultStudentOptions =
    internshipData?.students?.map((id) => ({
      label: `Student ${id}`, // Placeholder, ideal would be to fetch details
      value: id,
    })) || [];

  const defaultBatchOptions =
    internshipData?.batches?.map((id) => ({
      label: `Batch ${id}`,
      value: id,
    })) || [];

  const defaultDepartmentOptions =
    internshipData?.departments?.map((id) => ({
      label: `Dept ${id}`,
      value: id,
    })) || [];

  const defaultCompanyOptions = internshipData?.company
    ? [
        {
          label: `Company ${internshipData.company}`,
          value: internshipData.company,
        },
      ]
    : [];

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-h-[90vh] overflow-y-auto sm:max-w-2xl"
        onInteractOutside={(e) => {
          e.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle>
            {mode === "create" ? "Add New Internship" : "Edit Internship"}
          </DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(handleSubmit)}
            className="grid grid-cols-1 gap-4 py-2 md:grid-cols-2"
          >
            {/* Position Title */}
            <InputField<InternshipFormType>
              type="text"
              name="position"
              label="Position Title"
              placeholder="e.g. Software Development Intern"
              required
              disabled={isPending}
            />

            {/* Company/Vendor - Async Single Select */}
            <AsyncSingleSelectField<InternshipFormType>
              name="company"
              label="Company/Vendor"
              placeholder="Select company"
              fetchOptions={fetchInternVendorOptions}
              queryKey={["internVendors"]}
              defaultOptions={defaultCompanyOptions}
              required
              disabled={isPending}
            />

            {/* Select Students - Async Multi Select */}
            <AsyncMultiSelectField<InternshipFormType>
              name="students"
              label="Select Students"
              placeholder="Select students for this internship"
              fetchOptions={fetchStudentOptions}
              queryKey={["students"]}
              defaultOptions={defaultStudentOptions}
              disabled={isPending}
              className={{ itemClass: "md:col-span-2" }}
            />

            {/* Departments - Async Multi Select */}

            <AsyncMultiSelectField<InternshipFormType>
              name="departments"
              label="Departments"
              placeholder="Select departments"
              fetchOptions={departmentService.fetchDepartmentOptions}
              queryKey={["departments"]}
              defaultOptions={defaultDepartmentOptions}
              disabled={isPending}
              className={{ itemClass: "md:col-span-1" }}
            />

            {/* Batches - Async Multi Select */}

            <AsyncMultiSelectField<InternshipFormType>
              name="batches"
              label="Batches"
              placeholder="Select batches"
              fetchOptions={fetchBatchOptions}
              queryKey={["batches"]}
              defaultOptions={defaultBatchOptions}
              disabled={isPending}
              className={{ itemClass: "md:col-span-1" }}
            />

            {/* Duration */}
            <InputField<InternshipFormType>
              type="text"
              name="duration"
              label="Duration"
              placeholder="e.g. 3 months"
              required
              disabled={isPending}
            />

            {/* Stipend */}
            <InputField<InternshipFormType>
              type="number"
              name="stipend"
              label="Stipend"
              placeholder="e.g. 2500"
              disabled={isPending}
            />

            {/* Mode */}
            <SelectField<InternshipFormType>
              name="mode"
              label="Mode"
              options={MODE_OPTIONS}
              required
              disabled={isPending}
              className={{ itemClass: "md:col-span-1" }}
            />

            {/* Start Date */}
            <InputField<InternshipFormType>
              type="date"
              name="startDate"
              label="Start Date"
              required
              disabled={isPending}
            />

            {/* End Date */}
            <InputField<InternshipFormType>
              type="date"
              name="endDate"
              label="End Date"
              disabled={isPending}
            />

            {/* Status */}
            <SelectField<InternshipFormType>
              name="status"
              label="Status"
              options={STATUS_OPTIONS}
              required
              disabled={isPending}
              className={{ itemClass: "md:col-span-1" }}
            />

            {/* Description */}
            <TextareaField<InternshipFormType>
              name="description"
              label="Description"
              placeholder="Brief description about the internship"
              disabled={isPending}
              className={{ itemClass: "md:col-span-2" }}
            />

            {/* Requirements */}
            <TextareaField<InternshipFormType>
              name="requirements"
              label="Requirements"
              placeholder="Skills, qualifications, or other requirements"
              disabled={isPending}
              className={{ itemClass: "md:col-span-2" }}
            />

            <DialogFooter className="md:col-span-2">
              <Button
                type="button"
                variant="outline"
                onClick={onCancel}
                disabled={isPending}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isPending}>
                {isPending
                  ? mode === "create"
                    ? "Adding..."
                    : "Saving..."
                  : mode === "create"
                    ? "Create Internship"
                    : "Save Changes"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default InternshipDialog;
